
import React, { useState } from 'react';
import { CustomerContact, CustomerMessage } from '@/types/resellerCustomer';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { format } from 'date-fns';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  MessageSquare, 
  Send, 
  Paperclip, 
  Clock,
  FileText,
  User,
  Check,
  X,
  Upload,
  AlignLeft,
  ArrowRight
} from "lucide-react";

interface CommunicationCenterProps {
  contact: CustomerContact;
  messages: CustomerMessage[];
  onSendMessage: (message: Omit<CustomerMessage, 'id' | 'sentAt'>) => Promise<boolean>;
  onBack: () => void;
}

const CommunicationCenter: React.FC<CommunicationCenterProps> = ({
  contact,
  messages,
  onSendMessage,
  onBack
}) => {
  const [messageType, setMessageType] = useState<'email' | 'sms' | 'note'>('email');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  // Group messages by date for better display
  const groupedMessages = messages.reduce((groups, message) => {
    const date = format(message.sentAt, 'yyyy-MM-dd');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {} as Record<string, CustomerMessage[]>);
  
  // Sort dates in descending order (newest first)
  const sortedDates = Object.keys(groupedMessages).sort().reverse();
  
  const handleSend = async () => {
    if (!content.trim()) return;
    
    setIsSending(true);
    try {
      await onSendMessage({
        customerId: contact.id,
        type: messageType,
        direction: 'outbound',
        content,
        subject: messageType === 'email' ? subject : undefined,
        status: 'sent',
      });
      
      // Clear fields after successful send
      setContent('');
      if (messageType === 'email') setSubject('');
      
      // Show success notification
    } catch (error) {
      // Show error notification
    } finally {
      setIsSending(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Contact
          </Button>
          <span className="text-gray-500">|</span>
          <h2 className="text-xl font-semibold flex items-center">
            <span className="mr-2">Communications with {contact.name}</span>
            <Badge
              variant="outline"
              className={
                contact.status === 'active' ? 'bg-green-100 text-green-800 border-green-200' :
                contact.status === 'pending' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                'bg-gray-100 text-gray-800 border-gray-200'
              }
            >
              {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
            </Badge>
          </h2>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Info Sidebar */}
        <Card className="lg:row-span-2 h-fit">
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Name</h3>
              <p>{contact.name}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Email</h3>
              <div className="flex items-center">
                <Mail className="h-4 w-4 mr-2 text-gray-400" />
                <p className="text-sm">{contact.email}</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Phone</h3>
              <div className="flex items-center">
                <Phone className="h-4 w-4 mr-2 text-gray-400" />
                <p className="text-sm">{contact.phone}</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Company</h3>
              <p className="text-sm">{contact.company}</p>
              {contact.position && <p className="text-xs text-gray-500">{contact.position}</p>}
            </div>
            
            <div className="pt-2">
              <h3 className="text-sm font-medium mb-2">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" className="w-full">
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
                <Button size="sm" variant="outline" className="w-full">
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
                <Button size="sm" variant="outline" className="w-full">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  SMS
                </Button>
                <Button size="sm" variant="outline" className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  Note
                </Button>
              </div>
            </div>
            
            <div className="pt-2">
              <h3 className="text-sm font-medium mb-2">Recent Orders</h3>
              {contact.orderIds.length > 0 ? (
                <div className="space-y-2">
                  {contact.orderIds.slice(0, 3).map((orderId, index) => (
                    <div key={index} className="text-sm flex justify-between">
                      <span className="font-medium">{orderId}</span>
                      <Badge variant="outline" className="text-xs">Paid</Badge>
                    </div>
                  ))}
                  {contact.orderIds.length > 3 && (
                    <Button variant="link" size="sm" className="p-0 h-auto">
                      View all {contact.orderIds.length} orders
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No orders</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* New Message Composer */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>New Message</CardTitle>
            <CardDescription>
              Send an email, SMS, or record a note about this contact
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={messageType} onValueChange={(value) => setMessageType(value as any)}>
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="email" className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </TabsTrigger>
                <TabsTrigger value="sms" className="flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  SMS
                </TabsTrigger>
                <TabsTrigger value="note" className="flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  Note
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="email" className="space-y-4">
                <div>
                  <p className="text-sm mb-1">To: {contact.name} ({contact.email})</p>
                  <Input
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
                <Textarea
                  placeholder="Write your email message..."
                  className="min-h-[200px]"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                />
                <div className="flex justify-between items-center">
                  <Button variant="outline" size="sm" className="gap-1">
                    <Paperclip className="h-4 w-4" />
                    Attach
                  </Button>
                  <Button onClick={handleSend} disabled={isSending || !content.trim()}>
                    {isSending ? (
                      <>
                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="sms" className="space-y-4">
                <div>
                  <p className="text-sm mb-1">To: {contact.name} ({contact.phone})</p>
                </div>
                <Textarea
                  placeholder="Write your SMS message..."
                  className="min-h-[150px]"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                />
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    <span className="font-medium">{content.length}</span>/160 characters
                  </div>
                  <Button onClick={handleSend} disabled={isSending || !content.trim()}>
                    {isSending ? (
                      <>
                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send SMS
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="note" className="space-y-4">
                <Textarea
                  placeholder="Write a note about this contact..."
                  className="min-h-[200px]"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                />
                <div className="flex justify-end">
                  <Button onClick={handleSend} disabled={isSending || !content.trim()}>
                    {isSending ? (
                      <>
                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Save Note
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* Message History */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Message History</CardTitle>
            <CardDescription>
              View all communications with this contact
            </CardDescription>
          </CardHeader>
          <CardContent>
            {messages.length > 0 ? (
              <div className="space-y-6">
                {sortedDates.map(date => (
                  <div key={date}>
                    <div className="relative flex items-center py-2">
                      <div className="flex-grow border-t border-gray-200"></div>
                      <span className="flex-shrink mx-4 text-gray-400 text-sm">
                        {format(new Date(date), 'MMMM d, yyyy')}
                      </span>
                      <div className="flex-grow border-t border-gray-200"></div>
                    </div>
                    
                    <div className="space-y-4">
                      {groupedMessages[date].map(message => (
                        <div 
                          key={message.id} 
                          className={`p-4 rounded-lg ${
                            message.direction === 'outbound' 
                              ? 'bg-primary/10 ml-12' 
                              : 'bg-gray-100 mr-12'
                          }`}
                        >
                          <div className="flex justify-between mb-2">
                            <div className="flex items-center">
                              <div className={`p-1 rounded-full ${
                                message.type === 'email' ? 'bg-blue-100' :
                                message.type === 'sms' ? 'bg-green-100' :
                                'bg-gray-100'
                              }`}>
                                {message.type === 'email' ? (
                                  <Mail className="h-3 w-3 text-blue-600" />
                                ) : message.type === 'sms' ? (
                                  <MessageSquare className="h-3 w-3 text-green-600" />
                                ) : (
                                  <FileText className="h-3 w-3 text-gray-600" />
                                )}
                              </div>
                              <span className="ml-2 text-sm font-medium">
                                {message.type === 'email' ? 'Email' : 
                                 message.type === 'sms' ? 'SMS' : 'Note'}
                              </span>
                              {message.direction === 'outbound' ? (
                                <span className="ml-2 text-xs text-gray-500 flex items-center">
                                  <ArrowRight className="h-3 w-3 mr-1" />
                                  Sent
                                </span>
                              ) : (
                                <span className="ml-2 text-xs text-gray-500 flex items-center">
                                  <ArrowLeft className="h-3 w-3 mr-1" />
                                  Received
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center text-xs text-gray-500">
                              <Clock className="h-3 w-3 mr-1" />
                              {format(message.sentAt, 'h:mm a')}
                            </div>
                          </div>
                          
                          {message.subject && (
                            <div className="text-sm font-medium mb-1">
                              Subject: {message.subject}
                            </div>
                          )}
                          
                          <div className="text-sm">{message.content}</div>
                          
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">Attachments:</p>
                              {message.attachments.map((attachment, i) => (
                                <div key={i} className="flex items-center text-xs bg-white p-1 rounded border">
                                  <Paperclip className="h-3 w-3 mr-1 text-gray-400" />
                                  <span className="mr-2">{attachment.name}</span>
                                  <span className="text-gray-400">
                                    ({Math.round(attachment.size / 1024)} KB)
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No messages with this contact yet</p>
                <p className="text-sm text-gray-400 mb-4">
                  Send your first message using the composer above
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CommunicationCenter;
